import {useMemo} from 'react';

import {FieldCombobox, resolveOptionLabel} from '../FieldCombobox';
import {FieldInput} from '../FieldInput';

import type {FieldComboboxOption} from '../FieldCombobox';
import type {ReactNode} from 'react';

export interface BankBranchInfo {
  number: string;
  name: string;
}

export interface BankBranchFieldProps {
  branches: BankBranchInfo[];
  /** `false` when the selected bank has no branch directory (e.g. sandbox/fake banks) — falls back to free-text entry. */
  hasBranchLookup: boolean;
  value: string;
  onValueChange: (branchNumber: string) => void;
  label?: ReactNode;
  placeholder?: string;
  emptyText?: ReactNode;
  error?: string;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
  cornerBadge?: ReactNode;
  id?: string;
}

export function BankBranchField({
  branches,
  hasBranchLookup,
  value,
  onValueChange,
  label,
  placeholder,
  emptyText,
  error,
  loading,
  disabled,
  required,
  cornerBadge,
  id
}: BankBranchFieldProps) {
  const options = useMemo(() => buildBankBranchOptions(branches), [branches]);

  if (!hasBranchLookup) {
    return (
      <FieldInput
        id={id}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={e => onValueChange(e.target.value)}
        error={error}
        disabled={disabled}
        required={required}
        cornerBadge={cornerBadge}
        isLoading={loading}
      />
    );
  }

  return (
    <FieldCombobox
      id={id}
      label={label}
      placeholder={placeholder}
      emptyText={emptyText}
      options={options}
      value={value}
      onValueChange={onValueChange}
      error={error}
      loading={loading}
      disabled={disabled}
      required={required}
      cornerBadge={cornerBadge}
      renderValue={branchNumber => resolveOptionLabel(options, branchNumber)}
    />
  );
}

export function buildBankBranchOptions(branches: BankBranchInfo[]): FieldComboboxOption[] {
  return branches.map(branch => ({
    value: branch.number,
    searchText: `${branch.number} ${branch.name}`,
    label: (
      <span className="flex w-full items-center gap-2">
        <span className="font-semibold text-sm whitespace-nowrap">{branch.number}</span>
        <span className="text-caption text-fg-tertiary whitespace-nowrap">{branch.name}</span>
      </span>
    )
  }));
}
